import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle, Plus, Pencil, ExternalLink, Clock, Link2, FileText, Disc, Bookmark, Share2, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlbumDialog } from "@/components/budget/AlbumDialog";
import { TransactionDialog } from "@/components/budget/TransactionDialog";
import { BudgetShareDialog } from "@/components/budget/BudgetShareDialog";
import { fmtMoney, fmtMoney0, fmtDate } from "@/lib/format";
import { FUND_META, CORE_FUNDS, SPEND_SEGMENTS, paymentBadge, pct } from "@/lib/budget";

function Badge({ b }) {
  return <span title={b.hint || ""} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${b.cls}`}>{b.label}</span>;
}

export default function BudgetArtist() {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [albumDlg, setAlbumDlg] = useState({ open: false, album: null });
  const [txDlg, setTxDlg] = useState({ open: false, tx: null });
  const [shareOpen, setShareOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/budget/artists/${artistId}`).then((r) => setD(r.data)).catch(() => toast.error("Failed to load budget"));
  }, [artistId]);
  useEffect(() => { load(); }, [load]);

  if (d === null)
    return (
      <div className="min-h-screen"><TopBar />
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8"><Skeleton className="h-96 rounded-2xl" /></main>
      </div>
    );

  const a = d.artist;
  const contract = a.contract || {};
  const summ = a.summary || {};
  const albumTitle = (id) => d.albums.find((x) => x.id === id)?.title || d.albums.find((x) => x.id === id)?.slug || "—";
  const marketingIsMin = contract.marketing_minimum_cents != null;

  return (
    <div className="min-h-screen">
      <TopBar />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
        <button data-testid="budget-back" onClick={() => navigate("/budget")} className="mb-5 flex items-center gap-1.5 text-sm font-medium text-[#35606E] hover:underline">
          <ArrowLeft className="h-4 w-4" /> All budgets
        </button>

        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
          <div>
            <h1 className="font-display text-4xl font-extrabold tracking-tight">{a.name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-neutral-500">
              {a.linked_artist_id
                ? <button onClick={() => navigate(`/artists/${a.linked_artist_id}`)} className="inline-flex items-center gap-1 text-[#35606E] hover:underline"><Link2 className="h-3.5 w-3.5" /> Roster profile</button>
                : <span>Budget-only artist</span>}
              {a.contract_master_count != null && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#F7EFDD] px-2.5 py-0.5 text-xs">
                  <FileText className="h-3 w-3" /> Contract: {a.contract_master_count} masters total
                </span>
              )}
            </div>
          </div>
          <div data-testid="snapshot-banner" className="flex items-center gap-2 rounded-full border border-[#E7DFCC] bg-[#FFFDF8] px-3 py-1.5 text-xs text-neutral-500">
            <Clock className="h-3.5 w-3.5" /> Snapshot {a.as_of ? new Date(a.as_of).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""} · not a live sync
          </div>
        </div>

        <div className="mt-4">
          <Button data-testid="share-budget-btn" variant="outline" onClick={() => setShareOpen(true)} className="rounded-full border-[#35606E] text-[#35606E] hover:bg-[#35606E]/10">
            <Share2 className="mr-1.5 h-4 w-4" /> Share budget
          </Button>
        </div>

        {a.current_plan?.album_1_title && (
          <p className="mt-3 text-sm text-neutral-600">
            Current plan: <strong>{a.current_plan.album_1_title}</strong>
            {a.current_plan.album_1_tracks ? ` (${a.current_plan.album_1_tracks} tracks)` : ""}{a.current_plan.next_project ? `, then ${a.current_plan.next_project}` : ""}.
            <span className="text-neutral-400"> Kept separate from the contract baseline.</span>
          </p>
        )}

        {/* Needs review */}
        {d.needs_review.length > 0 && (
          <div data-testid="needs-review-panel" className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-amber-800"><AlertTriangle className="h-4 w-4" /> Needs review ({d.needs_review.length})</p>
            <ul className="mt-2 space-y-1.5 text-sm text-amber-900/90">
              {d.needs_review.map((n, i) => (
                <li key={i} className="flex gap-2"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-500" />{n.text}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Committed vs budget overview */}
        <h2 className="mt-10 font-display text-2xl font-bold">Committed vs budget</h2>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500">
          {SPEND_SEGMENTS.map((seg) => (
            <span key={seg.key} className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: seg.color }} /> {seg.label}</span>
          ))}
          <span className="text-neutral-400">· tap a card for line items</span>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {CORE_FUNDS.map((f) => {
            const fn = a.funds[f];
            const budget = a.budget[f];
            const committed = fn.committed_cents;
            const denom = budget || committed;
            const rem = fn.remaining_milestone_cents || 0;
            return (
              <button
                key={f}
                data-testid={`committed-${f}`}
                onClick={() => navigate(`/budget/${artistId}/${f}`)}
                className="pb-card group p-4 text-left transition-transform hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1 text-sm font-semibold">{FUND_META[f].label}<ChevronRight className="h-4 w-4 text-neutral-300 transition-transform group-hover:translate-x-0.5 group-hover:text-neutral-600" /></span>
                  <span className="num-display text-sm">{fmtMoney0(committed)}<span className="text-neutral-400"> / {budget ? fmtMoney0(budget) : "—"}</span></span>
                </div>
                <div className="mt-2 flex h-2.5 overflow-hidden rounded-full bg-neutral-200/70">
                  {SPEND_SEGMENTS.map((seg) => {
                    const w = pct(fn[seg.key] || 0, denom);
                    return w > 0 ? <div key={seg.key} title={`${seg.label}: ${fmtMoney0(fn[seg.key])}`} style={{ width: `${w}%`, backgroundColor: seg.color }} /> : null;
                  })}
                  {rem > 0 ? <div title={`Remaining: ${fmtMoney0(rem)}`} style={{ width: `${pct(rem, denom)}%`, backgroundColor: "#64748B" }} /> : null}
                </div>
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-neutral-500">
                  <span>Paid {fmtMoney0(fn.paid_cents)}</span>
                  {rem > 0 ? null : <span>Pending {fmtMoney0(fn.pending_cents)}</span>}
                  <span>Bookmarked {fmtMoney0(fn.bookmarked_cents)}</span>
                  {f !== "advance" && fn.review_cents > 0 ? <span data-testid={`committed-${f}-in-review`} className="font-medium text-amber-700">In review {fmtMoney0(fn.review_cents)}</span> : null}
                  {f !== "advance" && fn.unverified_cents > 0 ? <span data-testid={`committed-${f}-unverified`} className="font-medium text-red-700">Unverified {fmtMoney0(fn.unverified_cents)}</span> : null}
                  {rem > 0 ? (
                    <span className="inline-flex items-center gap-1 font-medium text-slate-600">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: "#64748B" }} /> Remaining {fmtMoney0(rem)}
                    </span>
                  ) : null}
                </div>
                {rem > 0 && fn.remaining_milestone_note ? (
                  <p className="mt-1 text-[11px] text-slate-500">{fn.remaining_milestone_note}</p>
                ) : null}
                {budget ? <p className="mt-1 text-[11px] text-neutral-400">Total to recoup {fmtMoney0(budget)}</p> : null}
              </button>
            );
          })}
        </div>

        {/* Contract baselines */}
        <h2 className="mt-10 font-display text-2xl font-bold">Contract baselines</h2>
        <p className="text-sm text-neutral-500">Original contract funds. Legal is tracked separately, outside the funds.</p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {["recording", "marketing", "advance", "legal_outside_funds"]
            .filter((f) => f !== "legal_outside_funds" || a.budget.legal_outside_funds != null || (a.funds.legal_outside_funds?.committed_cents || 0) > 0)
            .map((f) => {
            const meta = FUND_META[f];
            const s = summ[f] || {};
            const budget = a.budget[f];
            const fb = a.funds[f];
            return (
              <div key={f} data-testid={`contract-fund-${f}`} className="pb-card p-4">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: meta.color }} />
                  <span className="text-sm font-semibold">{meta.label}</span>
                </div>
                <p className="num-display mt-2 text-2xl font-extrabold">{f === "recording" && a.recording_mode === "covered_by_advance" ? <span className="text-base font-semibold text-neutral-500">None — covered by advance</span> : (budget == null ? "—" : fmtMoney0(budget))}</p>
                {f === "marketing" && marketingIsMin && <p className="text-[11px] text-neutral-400">minimum, not a cap</p>}
                {f === "advance" && contract.artist_advance_cents == null && a.recording_mode !== "covered_by_advance" && <p className="text-[11px] text-neutral-400">no separate advance in contract</p>}
                <dl className="mt-3 space-y-1 text-xs">
                  <Row label="Submitted (AP)" value={fmtMoney(s.ap_paid_group_cents ?? fb.submitted_cents)} hint="AP Paid group = submitted, not bank-confirmed" />
                  <Row label={s.requested_in_review_note ? "Requested · in review" : "Requested"} value={fmtMoney(s.ap_pending_requested_cents ?? fb.requested_cents)} hint={s.requested_in_review_note} />
                  {f !== "advance" && fb.review_cents > 0 ? <Row label="In review · approval unconfirmed" value={fmtMoney(fb.review_cents)} hint="AP in review — not approved, not requested" /> : null}
                  {(s.email_receipt_only_cents || fb.email_receipt_cents) ? <Row label="Email receipts" value={fmtMoney(s.email_receipt_only_cents ?? fb.email_receipt_cents)} /> : null}
                  {(fb.reported_cents || s.email_reported_gross_cents || s.email_reported_paid_cents) ? <Row label="Reported by email" value={fmtMoney(fb.reported_cents || s.email_reported_gross_cents || s.email_reported_paid_cents)} /> : null}
                  <Row label="Cleared (bank)" value={s.amount_verified_cleared_cents ? fmtMoney(s.amount_verified_cleared_cents) : "None confirmed"} />
                </dl>
              </div>
            );
          })}
        </div>

        {/* Albums */}
        <div className="mt-10 flex items-center justify-between">
          <div>
            <h2 className="font-display text-2xl font-bold">Albums</h2>
            <p className="text-sm text-neutral-500">Every single rolls up under an album. Spend is from tracked transactions.</p>
          </div>
          <Button data-testid="add-album-btn" onClick={() => setAlbumDlg({ open: true, album: null })} className="rounded-full bg-[#35606E] hover:bg-[#294b57]">
            <Plus className="mr-1.5 h-4 w-4" /> Add album
          </Button>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {d.albums.map((al) => (
            <div key={al.id} data-testid={`album-card-${al.id}`} className="pb-card p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="flex items-center gap-1.5 truncate font-display text-lg font-bold"><Disc className="h-4 w-4 text-neutral-400" /> {al.title || "Untitled"}</h3>
                  <p className="mt-0.5 text-xs text-neutral-500">
                    <span className="uppercase">{al.kind}</span>
                    {al.planned_tracks ? ` · ${al.planned_tracks} tracks` : al.masters ? ` · ${al.masters} masters` : ""}
                    {al.historical ? " · historical" : ""} · {al.tx_count} tx
                  </p>
                </div>
                <button data-testid={`edit-album-${al.id}`} onClick={() => setAlbumDlg({ open: true, album: al })} className="rounded-full p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"><Pencil className="h-4 w-4" /></button>
              </div>
              <div className="mt-3 space-y-3">
                {["recording", "marketing"].map((f) => {
                  const fn = al.funds[f];
                  const hasAlloc = fn.allocation_cents != null;
                  const cap = fn.allocation_cents;
                  const committed = fn.committed_cents;
                  const remaining = hasAlloc ? cap - committed : null;
                  const rawPct = hasAlloc && cap > 0 ? Math.round((committed / cap) * 100) : 0;
                  const over = hasAlloc && committed > cap;
                  const warn = hasAlloc && !over && rawPct >= 90;
                  const barColor = over ? "#DC2626" : warn ? "#D97706" : null; // red / amber
                  return (
                    <div key={f}>
                      <div className="flex items-center justify-between gap-2 text-xs">
                        <span className="flex items-center gap-1.5 font-medium text-neutral-600">
                          {FUND_META[f].label}
                          {over && (
                            <span data-testid={`album-${al.id}-${f}-overcap`} className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-700">Over cap</span>
                          )}
                        </span>
                        <span className="num-display text-neutral-500">{fmtMoney0(committed)}<span className="text-neutral-400"> / {hasAlloc ? `Cap ${fmtMoney0(cap)}` : "Not allocated"}</span></span>
                      </div>
                      <div className="mt-1 flex h-2 overflow-hidden rounded-full bg-neutral-200/70">
                        {hasAlloc && barColor ? (
                          <div style={{ width: `${Math.min(100, rawPct)}%`, backgroundColor: barColor }} title={`${rawPct}% of cap`} />
                        ) : (
                          SPEND_SEGMENTS.map((seg) => {
                            const denom = hasAlloc ? cap : committed;
                            const w = pct(fn[seg.key] || 0, denom);
                            return w > 0 ? <div key={seg.key} title={`${seg.label}: ${fmtMoney0(fn[seg.key])}`} style={{ width: `${w}%`, backgroundColor: seg.color }} /> : null;
                          })
                        )}
                      </div>
                      {hasAlloc && (
                        <div data-testid={`album-${al.id}-${f}-used`} className="mt-1 flex justify-between text-[11px]">
                          <span className={over ? "font-medium text-red-600" : warn ? "font-medium text-amber-600" : "text-neutral-500"}>{rawPct}% of cap used</span>
                          <span className={over ? "font-medium text-red-600" : "text-neutral-500"}>{over ? `${fmtMoney0(-remaining)} over cap` : `${fmtMoney0(remaining)} under cap`}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <div data-testid="unassigned-card" className="pb-card border-2 border-dashed border-neutral-300 bg-transparent p-4">
            <h3 className="font-display text-lg font-bold text-neutral-600">Unassigned</h3>
            <p className="mt-0.5 text-xs text-neutral-500">{d.unassigned.tx_count} tx · {d.unassigned.single_count} single(s) awaiting an album</p>
            <div className="mt-3 space-y-1.5 text-xs">
              {CORE_FUNDS.map((f) => d.unassigned.funds[f].tx_count > 0 && (
                <div key={f} className="flex justify-between"><span className="text-neutral-500">{FUND_META[f].label}</span><span className="num-display">{fmtMoney0(d.unassigned.funds[f].spent_cents)}</span></div>
              ))}
              {d.unassigned.tx_count === 0 && <p className="text-neutral-400">Everything is allocated.</p>}
            </div>
          </div>
        </div>

        {/* Transactions */}
        <div className="mt-10 flex items-center justify-between">
          <div>
            <h2 className="font-display text-2xl font-bold">Transactions</h2>
            <p className="text-sm text-neutral-500">{d.transactions.length} rows from AP, provider receipts and email-reported payments.</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="add-bookmark-btn" variant="outline" onClick={() => setTxDlg({ open: true, tx: { bookmarked: true, fund: "recording" } })} className="rounded-full border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-50">
              <Bookmark className="mr-1.5 h-4 w-4" /> Add bookmarked cost
            </Button>
            <Button data-testid="add-tx-btn" onClick={() => setTxDlg({ open: true, tx: null })} className="rounded-full bg-[#35606E] hover:bg-[#294b57]">
              <Plus className="mr-1.5 h-4 w-4" /> Add transaction
            </Button>
          </div>
        </div>
        <div className="pb-card mt-4 overflow-x-auto p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Fund</TableHead>
                <TableHead>Album</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {d.transactions.map((t) => {
                const b = paymentBadge(t);
                const url = t.source?.url;
                return (
                  <TableRow key={t.id} data-testid={`tx-row-${t.id}`} className={t.include_in_rollup ? "" : "opacity-60"}>
                    <TableCell className="whitespace-nowrap text-xs text-neutral-500">{t.date ? fmtDate(t.date) : "—"}</TableCell>
                    <TableCell className="max-w-[140px] truncate text-sm font-medium">{t.vendor || "—"}</TableCell>
                    <TableCell className="max-w-[240px] truncate text-sm text-neutral-600" title={t.description || ""}>
                      {t.description || "—"}
                      {t.note && <span title={t.note} className="ml-1 text-neutral-400">ⓘ</span>}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs">
                      <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: FUND_META[t.fund]?.color }} />{FUND_META[t.fund]?.label || t.fund}</span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-neutral-500">{t.album_id ? albumTitle(t.album_id) : (t.is_single ? "Single (unassigned)" : "Not allocated")}</TableCell>
                    <TableCell className="num-display whitespace-nowrap text-right text-sm font-semibold">{fmtMoney(t.amount_cents)}</TableCell>
                    <TableCell><Badge b={b} /></TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      {url && <a href={url} target="_blank" rel="noreferrer" title="Open source" className="inline-flex p-1 text-neutral-400 hover:text-[#35606E]"><ExternalLink className="h-3.5 w-3.5" /></a>}
                      <button data-testid={`edit-tx-${t.id}`} onClick={() => setTxDlg({ open: true, tx: t })} className="inline-flex p-1 text-neutral-400 hover:text-neutral-700"><Pencil className="h-3.5 w-3.5" /></button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </main>

      <AlbumDialog open={albumDlg.open} onOpenChange={(o) => setAlbumDlg((s) => ({ ...s, open: o }))} artistId={artistId} album={albumDlg.album} onSaved={load} />
      <TransactionDialog open={txDlg.open} onOpenChange={(o) => setTxDlg((s) => ({ ...s, open: o }))} artistId={artistId} albums={d.albums} tx={txDlg.tx} onSaved={load} />
      <BudgetShareDialog open={shareOpen} onOpenChange={setShareOpen} artistId={artistId} artistName={a.name} />
    </div>
  );
}

function Row({ label, value, hint }) {
  return (
    <div className="flex items-center justify-between" title={hint || ""}>
      <dt className="text-neutral-500">{label}</dt>
      <dd className="num-display font-medium">{value}</dd>
    </div>
  );
}
